import React from 'react'
import PropTypes from 'prop-types'
import { findIndex, last } from 'lodash'
import { css, StyleSheet } from 'aphrodite/no-important'
import { colors } from '@lmc/common/constants'
import dayjs from 'dayjs'
import { darken, lighten } from '../../../../../LEGACY/colorUtils'

const LogRevisionItem = ({
  changelog,
  isCurrent,
  log,
  revision,
  showEdited,
  testId,
}) => {
  /**
   * The `revision` prop can be either a LogRevision item or a Log item.
   * Revisions use `revokedBy` for the carer who created the revision, while
   * Logs use `editedBy` for the carer who most recently created a revision.
   * The same goes for `revokedAt` vs. `editedAt`
   */
  const editedCarer = revision.revokedBy || revision.editedBy
  const editedTime = revision.revokedAt || revision.editedAt
  const revisionIndex = findIndex(log.logRevisions, ['id', revision.id])

  const isLogRestoredRevision = () => {
    if (revisionIndex > 0) {
      const previousRevisionIndex = revisionIndex - 1
      return log.logRevisions[previousRevisionIndex]['deleted']
    } else if (isCurrent) {
      return last(log.logRevisions)['deleted']
    } else {
      return false
    }
  }

  const isRestored = isLogRestoredRevision()

  return (
    <div className={css(classes.wrapper)} data-testid={testId || 'log-revision-item'}>
      <div className={css(classes.titleContainer)}>
        <span className={css(classes.smallText)}>
          {formatDate(revision.timeLogged)} - {formatName(log?.loggedBy)}
        </span>
        {isCurrent ? (
          revision.deleted ? (
            <div className={css(classes.badgeRemoved)}>Removed</div>
          ) : (
            <div className={css(classes.badge)}>Current</div>
          )
        ) : null}
      </div>
      <h3 className={css(classes.title)}>
        {revision.deleted ? 'Log Removed' : revision.title}
      </h3>
      <span className={css(classes.description)}>
        {(!revision.deleted && !isRestored && revision.previousDescription) ||
          revision.description}
      </span>
      {showEdited && (
        <span className={css(classes.smallText)}>
          {revision.deleted
            ? 'Removed by '
            : isRestored
            ? 'Restored by '
            : 'Edited by '}
          {formatName(editedCarer)} on {formatDate(editedTime)}
        </span>
      )}
      {!showEdited && log.doneBySu && (
        <span className={css(classes.suInvolved)}>
          Service user was involved in writing this log
        </span>
      )}
      {changelog.map((change, i) => (
        <div key={i} className={css(classes.smallText)}>
          {change}
        </div>
      ))}
    </div>
  )
}

const formatDate = (date) => dayjs(date).format('DD-MM-YYYY HH:mm')
const formatName = (item) =>
  item && item.name ? `${item.name.first} ${item.name.last}` : null

const classes = StyleSheet.create({
  badge: {
    background: lighten(colors.green, 80),
    color: darken(colors.green, 40),
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 'bold',
    marginLeft: 8,
    padding: '3px 12px 2px',
    borderRadius: 15,
  },
  badgeRemoved: {
    background: colors.grey,
    color: colors.black,
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 'bold',
    marginLeft: 8,
    padding: '3px 12px 2px',
  },
  description: {
    display: 'block',
    fontSize: 14,
    marginBottom: 2,
  },
  suInvolved: {
    color: '#595959',
    display: 'block',
    marginTop: 2,
    fontSize: '12px',
  },
  smallText: {
    color: colors.darkgrey,
    fontSize: 12,
  },
  title: {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: '16px',
    margin: '2px 0px',
  },
  titleContainer: {
    alignItems: 'center',
    display: 'flex',
  },
  wrapper: {
    paddingBottom: 20,
  },
})

LogRevisionItem.propTypes = {
  changelog: PropTypes.arrayOf(PropTypes.string),
  isCurrent: PropTypes.bool,
  log: PropTypes.object.isRequired,
  revision: PropTypes.object.isRequired,
  showEdited: PropTypes.bool,
  testId: PropTypes.string,
}

export default LogRevisionItem
